import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Optional fallback UI. Defaults to a centered error card. */
  fallback?: ReactNode
  /**
   * Called after the internal console.error. Use to forward the error to an
   * external reporter (e.g. Sentry.captureException) without coupling this
   * shared boundary to any specific SDK.
   */
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  error: Error | null
}

/**
 * AppErrorBoundary — top-level React error boundary for all SPAs.
 *
 * Behaviour:
 * - Always: console.error so Railway / hosted log aggregators capture it.
 * - With onError prop: forwards to caller (e.g. Sentry.captureException).
 *
 * Pass `onError={(err, info) => Sentry.captureException(err, { extra: info })}`
 * in SPAs that have Sentry configured.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, info)
    this.props.onError?.(error, info)
  }

  render() {
    if (this.state.error) {
      return this.props.fallback ?? <DefaultFallback error={this.state.error} />
    }
    return this.props.children
  }
}

// ── Internal helpers ───────────────────────────────────────────────────────

function reportError(error: Error, info: ErrorInfo) {
  // Both dev and prod: log to console (Railway captures stdout/stderr).
  // External reporters (Sentry, etc.) receive errors via the onError prop.
  console.error('[AppErrorBoundary] Uncaught error:', error, info.componentStack)
}

function DefaultFallback({ error }: { error: Error }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        fontFamily: 'system-ui, sans-serif',
        gap: '1rem',
      }}
    >
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
        Something went wrong
      </h1>
      <p style={{ color: '#6b7280', margin: 0, textAlign: 'center' }}>
        An unexpected error occurred. Reload the page or contact support if it
        persists.
      </p>
      {import.meta.env.DEV && (
        <pre
          style={{
            background: '#fee2e2',
            color: '#991b1b',
            padding: '1rem',
            borderRadius: '0.5rem',
            fontSize: '0.75rem',
            maxWidth: '640px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {error.message}
          {error.stack ? `\n\n${error.stack}` : ''}
        </pre>
      )}
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '0.5rem 1.25rem',
          background: '#2563eb',
          color: '#fff',
          border: 'none',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          fontSize: '0.875rem',
        }}
      >
        Reload page
      </button>
    </div>
  )
}
